import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, ExternalLink } from "lucide-react";
import { AppButton } from "@libs/zapi-ui/custom";
import { Card, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@libs/zapi-ui/shadcn";
import { PaginationControls } from "@/zapi-admin/pages/AiLogsPage/ui/PaginationControls";
import { getRouteClaudeRun } from "@/zapi-admin/shared/const/router";
import { downloadClaudeRunZip } from "../model/api";
import { RUN_KIND_LABEL, type TClaudeRun } from "../model/types";
import { formatBytes, formatCost, formatDateTime, formatDuration, runDurationMs } from "../lib/format";
import { RunStatusBadge } from "./RunStatusBadge";

type TClaudeRunsTableProps = {
  runs: TClaudeRun[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
};

const headClass = "text-zinc-600 text-[13px] whitespace-nowrap";
const cellClass = "text-[13px] text-zinc-800 align-top";

export function LogCell({ run }: { run: TClaudeRun }) {
  const [downloading, setDownloading] = useState(false);
  if (run.logStatus === "stored") {
    return (
      <AppButton
        appearance="text"
        color="zinc950"
        size="sm"
        disabled={downloading}
        className="flex items-center gap-1 px-1 whitespace-nowrap"
        onClick={async (e) => {
          e.stopPropagation();
          setDownloading(true);
          try {
            await downloadClaudeRunZip(run.id);
          } finally {
            setDownloading(false);
          }
        }}
        data-testid={`run-download-${run.id}`}
      >
        <Download size={14} />
        {downloading ? "Preparing…" : `zip · ${formatBytes(run.logBytes)}`}
      </AppButton>
    );
  }
  if (run.logStatus === "collecting") return <span className="text-zinc-500">collecting…</span>;
  if (run.logStatus === "failed") return <span className="text-red-700">not stored</span>;
  return <span className="text-zinc-400">—</span>;
}

export function ClaudeRunsTable({ runs, total, page, pageSize, setPage }: TClaudeRunsTableProps) {
  const navigate = useNavigate();

  return (
    <Card className="py-0 rounded-[10px] border-zinc-150 border gap-0" data-testid="claude-runs-table-card">
      <div className="overflow-x-auto">
        <Table className="p-[12px]" data-testid="claude-runs-table">
          <TableHeader>
            <TableRow>
              <TableHead className={headClass}>Started</TableHead>
              <TableHead className={headClass}>Kind</TableHead>
              <TableHead className={headClass}>Status</TableHead>
              <TableHead className={headClass}>User message</TableHead>
              <TableHead className={`${headClass} min-w-[220px]`}>Project</TableHead>
              <TableHead className={headClass}>User</TableHead>
              <TableHead className={headClass}>Organization</TableHead>
              <TableHead className={headClass}>Model</TableHead>
              <TableHead className={headClass}>Duration</TableHead>
              <TableHead className={headClass}>Cost</TableHead>
              <TableHead className={headClass}>Turns / tools</TableHead>
              <TableHead className={headClass}>Log</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} className="text-center text-zinc-500 text-[14px] py-8">
                  No runs match these filters.
                </TableCell>
              </TableRow>
            )}
            {runs.map((run) => (
              <TableRow
                key={run.id}
                className="cursor-pointer hover:bg-zinc-50"
                onClick={() => navigate(getRouteClaudeRun(run.id))}
                data-testid={`run-row-${run.id}`}
              >
                <TableCell className={`${cellClass} whitespace-nowrap`}>
                  {formatDateTime(run.startedAt ?? run.createdAt)}
                </TableCell>
                <TableCell className={`${cellClass} whitespace-nowrap`}>{RUN_KIND_LABEL[run.kind]}</TableCell>
                <TableCell className={cellClass}>
                  <RunStatusBadge status={run.status} />
                </TableCell>
                <TableCell className={cellClass}>
                  {run.userMessage ? (
                    <span className="block max-w-[280px] truncate" title={run.userMessage}>
                      {run.userMessage}
                    </span>
                  ) : (
                    <span className="text-zinc-400">—</span>
                  )}
                </TableCell>
                <TableCell className={cellClass}>
                  {run.project ? (
                    <span className="flex items-start gap-1">
                      <span>
                        <span className="text-zinc-500">{run.project.id}</span> {run.project.name}
                      </span>
                      {run.projectUrl && (
                        <a
                          href={run.projectUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-zinc-500 hover:text-zinc-900 shrink-0 mt-[2px]"
                          title="Open the project in the platform"
                          data-testid={`run-project-link-${run.id}`}
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </span>
                  ) : (
                    <span className="text-zinc-400">{run.projectId ? `deleted (${run.projectId})` : "—"}</span>
                  )}
                </TableCell>
                <TableCell className={cellClass}>
                  {run.user ? (
                    <span title={run.user.email}>
                      {run.user.firstName} {run.user.lastName}
                    </span>
                  ) : (
                    <span className="text-zinc-400">—</span>
                  )}
                </TableCell>
                <TableCell className={cellClass}>{run.organization.name}</TableCell>
                <TableCell className={`${cellClass} whitespace-nowrap`}>
                  {run.model ?? run.mode}
                  {run.effort && <span className="text-zinc-500"> {run.effort}</span>}
                  <span className="text-zinc-400"> · {run.launch}</span>
                </TableCell>
                <TableCell className={`${cellClass} whitespace-nowrap`}>{formatDuration(runDurationMs(run))}</TableCell>
                <TableCell className={`${cellClass} whitespace-nowrap`}>{formatCost(run.estCostUsd)}</TableCell>
                <TableCell className={`${cellClass} whitespace-nowrap`}>
                  {run.assistantTurns ?? "—"} / {run.toolCalls ?? "—"}
                </TableCell>
                <TableCell className={cellClass}>
                  <LogCell run={run} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <PaginationControls page={page} total={total} pageSize={pageSize} setPage={setPage} />
    </Card>
  );
}
